import type {
  Conversation,
  InboxConversationStatus,
  InboxDemoData,
  InboxGroup,
  Message,
} from "../data/types";

const VIEW_STATUSES: InboxConversationStatus[] = [
  "ai",
  "pending",
  "mine",
  "closed",
];

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

export function cloneDemoData(data: InboxDemoData): InboxDemoData {
  return {
    ...data,
    navigation: data.navigation ? { ...data.navigation } : undefined,
    groups: data.groups.map(cloneGroup),
    conversations: data.conversations.map((conversation) => ({
      ...conversation,
      messages: conversation.messages.map((message) => ({ ...message })),
    })),
  };
}

function cloneGroup(group: InboxGroup): InboxGroup {
  return {
    ...group,
    children: group.children?.map(cloneGroup),
  };
}

export function collectExpandedGroupIds(groups: InboxGroup[]): string[] {
  const ids: string[] = [];

  const walk = (items: InboxGroup[]) => {
    items.forEach((item) => {
      const hasChildren = Boolean(item.children?.length);
      if (hasChildren && item.defaultExpanded !== false) {
        ids.push(item.id);
      }
      if (item.children) walk(item.children);
    });
  };

  walk(groups);
  return ids;
}

export function createLocalMessage(content: string, senderName?: string): Message {
  const now = new Date();
  const timestamp = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

  return {
    id: `local-${now.getTime()}`,
    sender: "human",
    content,
    timestamp,
    senderName,
  };
}

export function withAppendedMessage(
  conversations: Conversation[],
  conversationId: string,
  message: Message,
): Conversation[] {
  return conversations.map((conversation) => {
    if (conversation.id !== conversationId) return conversation;
    return {
      ...conversation,
      preview: message.content,
      timestamp: "刚刚",
      messages: [...conversation.messages, message],
    };
  });
}

const PHOTO_AVATARS = [
  "/images/inbox-demo/avatar/avatar1.png",
  "/images/inbox-demo/avatar/avatar2.png",
  "/images/inbox-demo/avatar/avatar3.png",
  "/images/inbox-demo/avatar/avatar4.png",
  "/images/inbox-demo/avatar/avatar5.png",
  "/images/inbox-demo/avatar/avatar6.png",
  "/images/inbox-demo/avatar/avatar7.png",
  "/images/inbox-demo/avatar/avatar8.png",
  "/images/inbox-demo/avatar/avatar9.png",
  "/images/inbox-demo/avatar/avatar10.png",
];

const CHANNELS_WITHOUT_PHOTO_AVATAR = new Set(["email", "livechat", "api"]);

const assignedPhotoAvatars = new Map<string, string>();
let shuffledPhotoAvatars: string[] | null = null;

function getShuffledPhotoAvatars(): string[] {
  if (shuffledPhotoAvatars) return shuffledPhotoAvatars;
  const pool = [...PHOTO_AVATARS];
  for (let index = pool.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [pool[index], pool[swapIndex]] = [pool[swapIndex], pool[index]];
  }
  shuffledPhotoAvatars = pool;
  return pool;
}

export function resolveConversationPhoto(
  conversation: Pick<Conversation, "id" | "avatar" | "fallbackAvatar" | "channelKey">,
): string | undefined {
  if (conversation.avatar) return conversation.avatar;
  if (
    conversation.channelKey &&
    CHANNELS_WITHOUT_PHOTO_AVATAR.has(conversation.channelKey)
  ) {
    return conversation.fallbackAvatar;
  }
  if (conversation.fallbackAvatar) return conversation.fallbackAvatar;

  const existing = assignedPhotoAvatars.get(conversation.id);
  if (existing) return existing;

  const pool = getShuffledPhotoAvatars();
  const src = pool[assignedPhotoAvatars.size % pool.length];
  assignedPhotoAvatars.set(conversation.id, src);
  return src;
}

export function conversationMatchesGroup(
  conversation: Conversation,
  groupId?: string,
): boolean {
  if (!groupId || groupId === "all" || groupId === "channels") return true;
  if (VIEW_STATUSES.indexOf(groupId as InboxConversationStatus) !== -1) {
    return conversation.status === groupId;
  }
  if (conversation.channelKey === groupId) return true;
  return conversation.id === groupId;
}

export function countConversationsForGroup(
  conversations: Conversation[],
  groupId: string,
): number {
  return conversations.filter((conversation) =>
    conversationMatchesGroup(conversation, groupId),
  ).length;
}

export function findGroupById(
  groups: InboxGroup[],
  groupId?: string,
): InboxGroup | undefined {
  if (!groupId) return undefined;

  const walk = (items: InboxGroup[]): InboxGroup | undefined => {
    for (const item of items) {
      if (item.id === groupId) return item;
      if (item.children) {
        const nested = walk(item.children);
        if (nested) return nested;
      }
    }
    return undefined;
  };

  return walk(groups);
}
